// ---------------------------------------------------------------------------
// YouAreYou (uru) — Landing Page
//
// Public homepage rendered at /. The primary conversion page for new visitors.
//
// Sections (top to bottom):
//   1. Hero — headline, subtitle, dual CTA buttons
//   2. Message Preview — iMessage-style trust link preview (the wow visual)
//   3. How It Works — 3 simple steps
//   4. Built for the Real World — 3 use-case cards
//   5. Security Highlights — 3 pillar cards
//   6. Trust Signals — compliance badges
//   7. FAQ — compact accordion
//   8. Final CTA — conversion banner
//   + Header (nav) and Footer reused from MarketingLayout patterns
//
// Design notes:
//   - Uses the same design system as all other pages (Public Sans, primary blue)
//   - Header/footer follow MarketingLayout conventions but are inline here
//     so the landing page has full control of its layout (no <Outlet> nesting)
//   - The iMessage preview card is the key visual differentiator — shows
//     the actual product experience when a trust link is sent
//   - Authenticated users who hit / are redirected to /dashboard via the
//     route config in App.tsx (this page only renders for unauthenticated users)
//
// Content sourced from:
//   - Hero mockup screenshots provided by product
//   - Use-case cards from "Built for the Real World" mockup
//   - iMessage preview from product mockup
//   - Security/FAQ content adapted from existing SecurityPage & HowItWorksPage
// ---------------------------------------------------------------------------

import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Icon from "@/components/ui/Icon";
import BrandName from "@/components/ui/BrandName";
import BrandMark from "@/components/ui/BrandMark";
import logoBrand from "@/assets/logo-brand-transparent2.jpg";
import AnimatedHeroLogo from "@/components/ui/AnimatedHeroLogo";
import LogoSpinner from "@/components/ui/LogoSpinner";
import step1Img from "@/assets/step2.png";
import step2AnimImg from "@/assets/step2-animation.apng";
import step2StaticImg from "@/assets/step2new.png";
import step3Img from "@/assets/step3.png";

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header() {
  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
        {/* Logo */}
        <Link to="/">
          <img src={logoBrand} alt="YouAreYou Verified" className="h-9 w-auto" />
        </Link>

        {/* Center nav — desktop */}
        <nav className="hidden items-center gap-6 md:flex">
          <Link
            to="/how-it-works"
            className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary"
          >
            How it works
          </Link>
          <Link
            to="/reality-check"
            className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary"
          >
            Reality Check
          </Link>
          <Link
            to="/security"
            className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary"
          >
            Security
          </Link>
          <Link
            to="/support"
            className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary"
          >
            Help
          </Link>
        </nav>

        {/* Right — auth buttons */}
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            className="text-sm font-semibold text-text-secondary transition-colors hover:text-text-primary"
          >
            Sign In
          </Link>
          <Link
            to="/register"
            className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Get Started
          </Link>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="flex items-center gap-4 overflow-x-auto border-t border-gray-100 px-4 py-2 md:hidden">
        <Link
          to="/how-it-works"
          className="shrink-0 text-xs font-medium text-text-secondary"
        >
          How it works
        </Link>
        <Link
          to="/reality-check"
          className="shrink-0 text-xs font-medium text-text-secondary"
        >
          Reality Check
        </Link>
        <Link
          to="/security"
          className="shrink-0 text-xs font-medium text-text-secondary"
        >
          Security
        </Link>
        <Link
          to="/support"
          className="shrink-0 text-xs font-medium text-text-secondary"
        >
          Help
        </Link>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function Footer() {
  return (
    <footer className="border-t border-gray-200 bg-white">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <BrandMark className="h-7 w-7" />
            <span className="text-sm font-bold">
              <BrandName />
            </span>
          </Link>

          {/* Links */}
          <div className="flex items-center gap-4 text-xs text-text-muted">
            <Link
              to="/privacy"
              className="transition-colors hover:text-text-secondary"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="transition-colors hover:text-text-secondary"
            >
              Terms of Service
            </Link>
            <Link
              to="/support"
              className="transition-colors hover:text-text-secondary"
            >
              Support
            </Link>
          </div>

          {/* Copyright */}
          <p className="text-[11px] text-text-muted">
            &copy; {new Date().getFullYear()} UrU.io. All rights reserved.
          </p>
        </div>

        {/* Powered by Mojo Verify */}
        <div className="mt-6 flex items-center justify-center gap-2 border-t border-gray-100 pt-5">
          <span className="text-xs text-text-muted">Powered by</span>
          <span className="text-xs font-bold text-text-secondary">
            Mojo Verify
          </span>
          <Icon
            name="verified_user"
            filled
            className="text-sm text-primary-500"
          />
        </div>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Section 2: Headline + CTAs (below the message thread)
// ---------------------------------------------------------------------------

// Message items for the animated thread
const THREAD_MESSAGES = [
  { id: "ask",    side: "incoming", kind: "text",   text: "Sure, let's do $200, I can come pick it up today — what's your address?" },
  { id: "send",   side: "outgoing", kind: "text",   text: "Jamie wants to know You Are You" },
  { id: "card",   side: "outgoing", kind: "card",   text: "" },
  { id: "reply",  side: "incoming", kind: "text",   text: "I'm not a bot but I guess a bot would say that... I'll verify now ✓" },
  { id: "status", side: "status",   kind: "status", text: "Ian Starnes confirmed ✓ Identity Verified" },
  { id: "thanks", side: "outgoing", kind: "text",   text: "Thanks for verifying! Here's my address, see you later 👋" },
] as const;

// Delays in ms for each message appearing
const MESSAGE_DELAYS = [1200, 3000, 4200, 6200, 8400, 10200];

function HeroSection() {
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    const timers = MESSAGE_DELAYS.map((delay, i) =>
      setTimeout(() => setVisibleCount(i + 1), delay),
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <section className="bg-gradient-to-b from-white to-background-light pb-12 pt-2 sm:pb-16 sm:pt-4">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-center lg:gap-16">

          {/* ---- Left: logo + headline + CTAs ---- */}
          <div className="w-full text-center lg:w-1/2 lg:text-left">
            {/* Logo */}
            <div className="mb-4 flex justify-center">
              <LogoSpinner size="xl" loop={false} />
            </div>

            {/* Brand name */}
            <div className="mb-6 flex justify-center">
              <AnimatedHeroLogo />
            </div>

            {/* Pill badge */}
            <div className="mb-5 inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-4 py-1.5">
              <Icon name="bolt" filled className="text-base text-primary-500" />
              <span className="text-sm font-semibold text-primary-600">
                Identity Verification in 30 Seconds
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl font-black leading-[1.1] tracking-tight text-text-primary sm:text-5xl md:text-6xl">
              Verify they're real.{" "}
              <span className="text-primary-500">Everyone's data stays private.</span>
            </h1>

            {/* Subtitle */}
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-text-secondary sm:text-xl lg:mx-0">
              The simplicity of a short link, the security of bank-grade identity verification.
              Create a Trust Link and know exactly who you&apos;re dealing with.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4 lg:justify-start">
              <Link
                to="/how-it-works"
                className="inline-flex items-center gap-2 rounded-xl bg-text-primary px-7 py-3.5 text-base font-bold text-white shadow-lg transition-all hover:bg-gray-800 hover:shadow-xl active:scale-[0.98]"
              >
                See how it works
                <Icon name="arrow_forward" className="text-lg" />
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-7 py-3.5 text-base font-bold text-text-primary shadow-sm transition-all hover:border-gray-400 hover:shadow-md"
              >
                Create a Trust Link
              </Link>
            </div>
          </div>

          {/* ---- Right: animated message thread ---- */}
          <div className="w-full lg:w-1/2">
            {/* Phone frame */}
            <div className="mx-auto max-w-sm rounded-3xl border border-gray-200 bg-gray-50 px-4 pb-6 pt-5 shadow-xl shadow-gray-200/60">
              {/* Status bar */}
              <div className="mb-3 flex items-center justify-between px-1">
                <span className="text-xs font-semibold text-text-muted">9:41 AM</span>
                <div className="flex items-center gap-1 text-text-muted">
                  <Icon name="signal_cellular_alt" className="text-sm" />
                  <Icon name="wifi" className="text-sm" />
                  <Icon name="battery_full" className="text-sm" />
                </div>
              </div>

              {/* Contact header */}
              <div className="mb-4 flex items-center gap-3 border-b border-gray-200 pb-4">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-300 text-sm font-bold text-white">
                  IS
                </div>
                <div>
                  <p className="text-sm font-semibold text-text-primary">Ian Starnes</p>
                  <p className="text-xs text-text-muted">Facebook Marketplace</p>
                </div>
              </div>

              {/* Messages */}
              <div className="space-y-2.5 min-h-[480px]">
                {THREAD_MESSAGES.map((msg, i) => {
                  const visible = visibleCount > i;

                  if (msg.kind === "status") {
                    return (
                      <div
                        key={msg.id}
                        className={`flex justify-center transition-all duration-500 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
                      >
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-[11px] font-semibold text-green-700">
                          <Icon name="verified" filled className="text-sm text-green-600" />
                          {msg.text}
                        </span>
                      </div>
                    );
                  }

                  if (msg.kind === "card") {
                    return (
                      <div
                        key={msg.id}
                        className={`flex justify-end transition-all duration-500 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
                      >
                        <div className="w-[72%] overflow-hidden rounded-2xl rounded-br-sm bg-white shadow-sm ring-1 ring-gray-200">
                          <div className="relative bg-gradient-to-br from-primary-500 to-primary-600 px-4 py-4">
                            <div className="absolute top-2.5 right-2.5 flex h-6 w-6 items-center justify-center rounded-full bg-white/20">
                              <Icon name="verified_user" filled className="text-xs text-white" />
                            </div>
                            <div className="flex items-center gap-2.5">
                              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 ring-2 ring-white/30">
                                <span className="text-sm font-bold text-white">JT</span>
                              </div>
                              <div>
                                <p className="text-[10px] font-semibold text-white/70">Verification Request from</p>
                                <p className="text-sm font-bold text-white">Jamie Turner</p>
                              </div>
                            </div>
                            <div className="mt-3">
                              <p className="text-xs font-semibold text-white/90">Trust who you talk to.</p>
                            </div>
                          </div>
                          <div className="px-3 py-2">
                            <p className="text-xs font-semibold text-text-primary">
                              Jamie wants to know <BrandName />
                            </p>
                            <p className="mt-0.5 text-[10px] text-text-muted">u-ru.io/jamie-verify-921</p>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={msg.id}
                      className={`flex transition-all duration-500 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"} ${msg.side === "outgoing" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[72%] rounded-2xl px-3.5 py-2.5 text-sm leading-snug ${
                          msg.side === "outgoing"
                            ? "rounded-br-sm bg-primary-500 font-medium text-white"
                            : "rounded-bl-sm bg-white text-text-primary shadow-sm ring-1 ring-gray-200"
                        }`}
                      >
                        {msg.text}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Message input bar */}
              <div className="mt-4 flex items-center gap-2 border-t border-gray-200 pt-3">
                <div className="flex flex-1 items-center rounded-full border border-gray-300 bg-white px-4 py-2">
                  <span className="text-sm text-gray-400">Message</span>
                </div>
                <button className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-300">
                  <Icon name="arrow_upward" className="text-sm text-white" />
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section 3: How It Works (3 steps)
// ---------------------------------------------------------------------------

const HOW_IT_WORKS_STEPS = [
  {
    number: 1,
    image: step1Img,
    title: "Send your Trust Link to Someone Unknown",
    description:
      "Share your link with someone you want verified as 'really them' — by text, email, or QR code.",
  },
  {
    number: 2,
    image: step2StaticImg,
    animImage: step2AnimImg,
    title: "They provide proof",
    description:
      "The recipient securely submits their ID, phone, and liveness check — only the verifications you require.",
  },
  {
    number: 3,
    image: step3Img,
    title: "Proceed with confidence",
    description:
      "You know they are who they say they are. They know you are too. Move forward with certainty.",
  },
];

/** Plays the step 2 APNG once after a short delay, then loops back to static. */
function AnimatedStep2Image() {
  const [animKey, setAnimKey] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => setAnimKey(1), 5000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <img
      key={animKey}
      src={animKey === 0 ? step2StaticImg : step2AnimImg}
      alt="Step 2 illustration"
      className="h-full w-auto object-contain"
    />
  );
}

function HowItWorksSection() {
  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold tracking-tight text-text-primary sm:text-4xl">
            Three steps to trust.
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-base text-text-secondary">
            Get verified and start sharing your identity in minutes — not days.
          </p>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-3">
          {HOW_IT_WORKS_STEPS.map((step) => (
            <div
              key={step.number}
              className="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
            >
              {/* Step image */}
              <div className="mb-4 flex h-28 items-center justify-center">
                {step.number === 2 ? (
                  <AnimatedStep2Image />
                ) : (
                  <img
                    src={step.image}
                    alt={`Step ${step.number} illustration`}
                    className="h-full w-auto object-contain"
                  />
                )}
              </div>
              {/* Step label */}
              <span className="mb-1 text-[11px] font-bold uppercase tracking-widest text-text-muted">
                Step {step.number}
              </span>
              <h3 className="text-sm font-semibold text-text-primary">
                {step.title}
              </h3>
              <p className="mt-1 text-xs text-text-secondary leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Privacy assurance note */}
        <div className="mt-6 flex items-start gap-2.5 rounded-xl border border-primary-100 bg-primary-50 px-4 py-3">
          <Icon
            name="shield"
            filled
            className="mt-0.5 shrink-0 text-lg text-primary-500"
          />
          <p className="text-xs text-text-secondary leading-relaxed">
            <span className="font-semibold text-text-primary">
              Verified without exposure.
            </span>{" "}
            By default, neither party ever sees the other's documents — you
            receive a trusted confirmation that everything checks out. Optional
            document sharing is coming soon for when you choose to share more.
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section 4: Built for the Real World (use-case cards)
// ---------------------------------------------------------------------------

const USE_CASES = [
  {
    icon: "open_in_new",
    title: "Marketplace Sales",
    description:
      "Selling a car or high-end electronics on Facebook? Send a Trust Link to ensure the buyer is real before meeting up.",
  },
  {
    icon: "handshake",
    title: "In-Person Meetups",
    description:
      "Meeting a stranger from the internet? Share a Trust Link first so both sides know they're meeting a real, verified person.",
  },
  {
    icon: "group",
    title: "Freelance Gigs",
    description:
      "Know exactly who you're hiring or working for. Build trust from the first message with a verified identity receipt.",
  },
];

function UseCasesSection() {
  return (
    <section className="bg-background-light py-16 sm:py-20">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold tracking-tight text-text-primary sm:text-4xl">
            Built for the <span className="text-primary">Real</span> World.
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-base text-text-secondary">
            Safely conduct business with anyone, anywhere.
          </p>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-3">
          {USE_CASES.map((useCase) => (
            <div
              key={useCase.title}
              className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100">
                <Icon
                  name={useCase.icon}
                  className="text-2xl text-text-secondary"
                />
              </div>
              <h3 className="mt-5 text-lg font-bold text-text-primary">
                {useCase.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-text-secondary">
                {useCase.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section 5: Security Highlights (3 pillars)
// ---------------------------------------------------------------------------

const SECURITY_PILLARS = [
  {
    icon: "visibility_off",
    iconBg: "bg-purple-50",
    iconColor: "text-purple-600",
    title: "Private Verification",
    description:
      "Recipients see that you're verified — without ever seeing your raw documents, SSN, or biometric data. Share your identity, not your paperwork.",
  },
  {
    icon: "passkey",
    iconBg: "bg-blue-50",
    iconColor: "text-primary-500",
    title: "Passwordless by Design",
    description:
      "No passwords to create, remember, or get stolen. Sign in with Face ID, Touch ID, or a one-time SMS code. Phishing-resistant by default.",
  },
  {
    icon: "encrypted",
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-600",
    title: "Encrypted by Default",
    description:
      "All data encrypted in transit (TLS 1.3) and at rest (AES-256). Biometric data is processed in a secure enclave and never stored in raw form.",
  },
];

function SecuritySection() {
  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold tracking-tight text-text-primary sm:text-4xl">
            Security you can trust.
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-base text-text-secondary">
            Your identity data is protected with bank-grade encryption and
            strict privacy controls at every step.
          </p>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-3">
          {SECURITY_PILLARS.map((pillar) => (
            <div
              key={pillar.title}
              className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-xl ${pillar.iconBg}`}
              >
                <Icon
                  name={pillar.icon}
                  className={`text-2xl ${pillar.iconColor}`}
                />
              </div>
              <h3 className="mt-5 text-lg font-bold text-text-primary">
                {pillar.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-text-secondary">
                {pillar.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section 6: Trust Signals (compliance badges)
// ---------------------------------------------------------------------------

const COMPLIANCE_BADGES = [
  "SOC 2 Type II",
  "GDPR Compliant",
  "CCPA Compliant",
  "KYC / AML",
  "WebAuthn L2",
  "TLS 1.3",
  "AES-256",
];

function TrustSignalsSection() {
  return (
    <section className="border-y border-gray-200 bg-background-light py-10 sm:py-12">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <p className="mb-5 text-center text-xs font-bold uppercase tracking-widest text-text-muted">
          Enterprise-grade compliance &amp; security standards
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {COMPLIANCE_BADGES.map((badge) => (
            <span
              key={badge}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-text-secondary shadow-sm"
            >
              <Icon
                name="check_circle"
                filled
                className="text-sm text-verified"
              />
              {badge}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section 7: FAQ (compact accordion)
// ---------------------------------------------------------------------------

const FAQ_ITEMS: { question: React.ReactNode; answer: React.ReactNode }[] = [
  {
    question: <>Is <BrandName /> free?</>,
    answer:
      "Yes. Verifying your identity and sharing your Trust Link is completely free for individuals. There are no hidden fees or subscriptions required to use the core service.",
  },
  {
    question: "How long does verification take?",
    answer:
      "Most verifications complete in under 2 minutes. You'll need a government-issued ID (driver's license or passport) and a device with a camera for the liveness check.",
  },
  {
    question: "Do I need to create a password?",
    answer: <><BrandName /> is completely passwordless. You sign in using Face ID, Touch ID, or a one-time SMS code. There are no passwords to create, remember, or get stolen.</>,
  },
  {
    question: "What can people see about me?",
    answer:
      "When someone views your Trust Link, they can see your name, your verified status, and which verification checks you've passed (e.g., Government ID, Liveness). They cannot see your actual ID documents, SSN, address, or any other sensitive data.",
  },
  {
    question: "What if I lose my phone?",
    answer:
      "Your account is secured by your phone number. On a new device, you can sign in using SMS verification. Your passkeys are device-specific, but you can always register new ones after signing in.",
  },
];

function FaqItem({
  question,
  answer,
  isOpen,
  onToggle,
}: {
  question: React.ReactNode;
  answer: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50"
        aria-expanded={isOpen}
      >
        <span className="text-sm font-semibold text-text-primary pr-4">
          {question}
        </span>
        <Icon
          name={isOpen ? "expand_less" : "expand_more"}
          className="shrink-0 text-xl text-text-muted"
        />
      </button>
      {isOpen && (
        <div className="px-5 pb-4">
          <p className="text-sm leading-relaxed text-text-secondary">
            {answer}
          </p>
        </div>
      )}
    </div>
  );
}

function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-2xl px-4 sm:px-6">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold tracking-tight text-text-primary sm:text-4xl">
            Frequently asked questions
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-base text-text-secondary">
            Everything you need to know about <BrandName />.
          </p>
        </div>

        <div className="mt-10 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          {FAQ_ITEMS.map((item, index) => (
            <FaqItem
              key={index}
              question={item.question}
              answer={item.answer}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section 8: Enterprise / Business KYC
// ---------------------------------------------------------------------------

const ENTERPRISE_FEATURES = [
  {
    icon: "api",
    title: "REST API & SDKs",
    description:
      "Drop our KYC API into your app in hours. Webhooks, event streams, and SDKs for web, iOS, and Android.",
  },
  {
    icon: "scale",
    title: "Built for volume",
    description:
      "From 100 to 10 million verifications. SLA-backed infrastructure with 99.99% uptime and global CDN delivery.",
  },
  {
    icon: "gavel",
    title: "Compliance-ready",
    description:
      "AML / KYC workflows, adverse media screening, PEP checks, and audit logs that satisfy regulators out of the box.",
  },
  {
    icon: "tune",
    title: "Configurable flows",
    description:
      "Choose which checks to run — Government ID, liveness, phone, email, professional licence — and white-label the entire experience.",
  },
  {
    icon: "support_agent",
    title: "Dedicated onboarding",
    description:
      "A solutions engineer works with your team from integration kickoff through go-live. No ticket queues.",
  },
  {
    icon: "lock",
    title: "Data residency & privacy",
    description:
      "Choose your data region. SOC 2 Type II certified. GDPR and CCPA compliant. Data never sold or shared.",
  },
];

function EnterpriseSection() {
  return (
    <section className="bg-background-dark py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary-700 bg-primary-900/60 px-4 py-1.5">
            <Icon name="business" className="text-base text-primary-400" />
            <span className="text-sm font-semibold text-primary-300">
              Enterprise &amp; Developer
            </span>
          </div>
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl">
            Need KYC for your business or app?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
            The same bank-grade identity verification that powers <BrandName /> is
            available as a fully white-labelled API. Embed it in your product,
            workflow, or onboarding funnel — and let us handle compliance.
          </p>
        </div>

        {/* Feature grid */}
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {ENTERPRISE_FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="rounded-2xl border border-slate-700/60 bg-slate-800/50 p-6"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-900/80">
                <Icon name={feature.icon} className="text-2xl text-primary-400" />
              </div>
              <h3 className="mt-4 text-base font-bold text-white">
                {feature.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl bg-gradient-to-br from-primary-600 to-primary-700 p-8 text-center sm:p-12">
          <h3 className="text-2xl font-extrabold text-white sm:text-3xl">
            Let&apos;s build something together.
          </h3>
          <p className="mx-auto mt-3 max-w-xl text-sm text-white/80 sm:text-base">
            Whether you&apos;re a startup adding KYC to your onboarding or an
            enterprise replacing a legacy compliance stack — we&apos;ll scope
            the right solution for your volume, region, and risk requirements.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4">
            <a
              href="mailto:enterprise@youareyou.io"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-7 py-3.5 text-base font-bold text-primary-600 shadow-sm transition-colors hover:bg-gray-50"
            >
              <Icon name="mail" className="text-lg" />
              Contact Enterprise Sales
            </a>
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-2 rounded-xl border border-white/30 px-7 py-3.5 text-base font-bold text-white transition-colors hover:bg-white/10"
            >
              See how it works
              <Icon name="arrow_forward" className="text-lg" />
            </Link>
          </div>
          <p className="mt-5 text-xs text-white/50">
            Typically responds within one business day. No sales pressure — just a technical conversation.
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section 9: Final CTA
// ---------------------------------------------------------------------------

function FinalCtaSection() {
  return (
    <section className="bg-background-light py-16 sm:py-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <div className="rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 p-8 text-center text-white shadow-xl sm:p-12">
          <h2 className="text-2xl font-extrabold sm:text-3xl">
            Ready to verify who you&apos;re dealing with?
          </h2>
          <p className="mx-auto mt-3 max-w-md text-sm text-white/80 sm:text-base">
            Join thousands of people who use <BrandName /> to prove their identity
            and build trust — for free.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-7 py-3.5 text-base font-bold text-primary-600 shadow-sm transition-colors hover:bg-gray-50"
            >
              Create a Trust Link
              <Icon name="arrow_forward" className="text-lg" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-xl border border-white/30 px-7 py-3.5 text-base font-bold text-white transition-colors hover:bg-white/10"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// LandingPage — Main Component
// ---------------------------------------------------------------------------

export default function LandingPage() {
  return (
    <div className="flex min-h-dvh flex-col bg-white">
      <Header />

      <main className="flex-1">
        <HeroSection />
        <UseCasesSection />
        <HowItWorksSection />
        <SecuritySection />
        <TrustSignalsSection />
        <FaqSection />
        <EnterpriseSection />
        <FinalCtaSection />
      </main>

      <Footer />
    </div>
  );
}
